import { useCallback, useEffect, useState } from 'react';
import { api, apiDownload, describeError } from '../api';
import { CountedField, FileButton, JsonEditor, Message, NAME_MAX, Pill, Section, fmtTime, useJsonText } from '../components/ui';
import { useSession } from '../session';

interface Summary { id: string; name: string; description: string; entries_count: number; token_estimate: number; global_enabled: boolean; is_seed?: boolean; source?: 'platform' | 'user'; updated_at: string; deleted_at?: string | null }
interface Entry { uid: number; key: string[]; keysecondary: string[]; comment: string; content: string; disable: boolean; constant: boolean; vectorized?: boolean; order: number; position: number; depth: number; [k: string]: unknown }
interface Book extends Summary { entries?: Entry[]; extensions: Record<string, unknown>; referenced_by: Array<{ character_id: string; name: string }> }

export function WorldBooksPage() {
  const { env, me } = useSession();
  const [books, setBooks] = useState<Summary[]>([]);
  const [book, setBook] = useState<Book | null>(null);
  const [name, setName] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!me) return;
    try {
      setBooks((await api<Summary[]>(`/api/world-books${includeDeleted ? '?include_deleted=1' : ''}`)).data);
    } catch (e) {
      setErr(describeError(e));
    }
  }, [me, includeDeleted]);
  useEffect(() => { void load(); }, [load, env]);

  const run = async (fn: () => Promise<string | void>) => {
    setErr(null); setMsg(null);
    try { const m = await fn(); if (m) setMsg(m); await load(); } catch (e) { setErr(describeError(e)); }
  };
  const open = (id: string) => run(async () => { setBook((await api<Book>(`/api/world-books/${id}`)).data); });
  const create = () => run(async () => { if (!name.trim()) return; const { data } = await api<Book>('/api/world-books', { method: 'POST', body: { name: name.trim() } }); setName(''); setBook(data); return `已创建 ${data.name}`; });
  const importFile = (file: File) => run(async () => {
    const json = JSON.parse(await file.text()) as { name?: string };
    const bookName = json.name || file.name.replace(/\.json$/i, '');
    const { data } = await api<Book>(`/api/world-books/import?name=${encodeURIComponent(bookName)}`, { method: 'POST', body: json });
    setBook(data);
    return `已导入 ${data.name}（${data.entries_count} 条）`;
  });
  /** add-mvp-protocol-gaps-3: the product's "global" switch = membership in wi_globals.world_info_settings.world_info.globalSelect (by name). */
  const toggleGlobal = (b: Summary) => run(async () => {
    await api(`/api/world-books/${b.id}`, { method: 'PATCH', body: { global_enabled: !b.global_enabled } });
    return `${b.name} 全局启用 → ${b.global_enabled ? '关' : '开'}`;
  });

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
      <Section title={`世界书 · ${books.length}`} actions={<FileButton label="导入 ST worlds/*.json" accept=".json" onFile={importFile} disabled={!me} />}>
        {!me && <Message text="请先登录" />}
        <div className="flex gap-2">
          <CountedField limit={NAME_MAX} placeholder="新建书名" value={name} onChange={setName} onKeyDown={(e) => e.key === 'Enter' && void create()} />
          <button className="btn" onClick={() => void create()}>新建</button>
        </div>
        <label className="flex items-center gap-1 text-xs text-zinc-400"><input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} /> 含已删除</label>
        <Message text={err} kind="error" />
        <Message text={msg} kind="ok" />
        <ul className="divide-y divide-zinc-800">
          {books.map((b) => (
            <li key={b.id} className={`cursor-pointer py-2 text-sm ${book?.id === b.id ? 'bg-zinc-800/40' : ''}`} onClick={() => void open(b.id)}>
              <div className="flex items-center gap-2">
                {b.name} <span className="font-mono text-[10px] text-zinc-600">{b.id.slice(0, 8)}</span> {b.source && <Pill tone={b.source === 'platform' ? 'indigo' : 'emerald'}>{b.source}</Pill>} <Pill>{b.entries_count} 条</Pill> <span title="token_estimate：启用条目 content + key 的 cl100k_base 计数（估算）"><Pill tone="amber">{b.token_estimate.toLocaleString()} tok</Pill></span>
                {b.global_enabled && <Pill tone="emerald">全局</Pill>} {b.deleted_at && <Pill tone="rose">已删除</Pill>}
                {!b.deleted_at && <button className="btn-ghost py-0 text-[11px]" title="PATCH /api/world-books/{id} { global_enabled }" onClick={(e) => { e.stopPropagation(); void toggleGlobal(b); }}>{b.global_enabled ? '关闭全局' : '开启全局'}</button>}
              </div>
              <div className="text-[11px] text-zinc-500">{fmtTime(b.updated_at)}</div>
            </li>
          ))}
        </ul>
      </Section>
      {book ? <BookDetail key={book.id + book.updated_at} book={book} onChanged={(b) => { setBook(b); void load(); }} onClosed={() => { setBook(null); void load(); }} /> : <Section title="条目"><div className="text-xs text-zinc-500">选择一本书。</div></Section>}
    </div>
  );
}

function BookDetail({ book, onChanged, onClosed }: { book: Book; onChanged: (b: Book) => void; onClosed: () => void }) {
  const locked = book.source === 'platform' || book.is_seed === true;
  const entries = book.entries ?? [];
  const all = useJsonText(entries);
  const [editing, setEditing] = useState<Entry | null>(null);
  const one = useJsonText(editing ?? {});
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [bookName, setBookName] = useState(book.name);

  const reload = async () => onChanged((await api<Book>(`/api/world-books/${book.id}`)).data);
  const run = async (fn: () => Promise<string | void>) => {
    setErr(null); setMsg(null);
    try { const m = await fn(); if (m) setMsg(m); await reload(); } catch (e) { setErr(describeError(e)); }
  };
  const saveAll = () => { const v = all.parse(); if (v === undefined) return; void run(async () => { await api(`/api/world-books/${book.id}/entries`, { method: 'PUT', body: { entries: v } }); return '已整体替换条目'; }); };
  const toggleVectorized = (e: Entry) => run(async () => { await api(`/api/world-books/${book.id}/entries/${e.uid}`, { method: 'PATCH', body: { vectorized: !e.vectorized } }); return `条目 ${e.uid} vectorized = ${String(!e.vectorized)}（需开启 vectors.enabled_world_info 才会语义激活）`; });
  const saveOne = () => { if (!editing) return; const v = one.parse() as Record<string, unknown> | undefined; if (!v) return; void run(async () => { await api(`/api/world-books/${book.id}/entries/${editing.uid}`, { method: 'PATCH', body: v }); setEditing(null); return `已更新条目 ${editing.uid}`; }); };
  const addEntry = () => run(async () => { const { data } = await api<Entry>(`/api/world-books/${book.id}/entries`, { method: 'POST', body: { key: ['new'], content: '', comment: 'New entry' } }); return `已新增条目 uid=${data.uid}`; });
  const delEntry = (uid: number) => run(async () => { await api(`/api/world-books/${book.id}/entries/${uid}`, { method: 'DELETE' }); return `已删除条目 ${uid}`; });
  const rename = () => run(async () => { const { data } = await api<Book>(`/api/world-books/${book.id}`, { method: 'PATCH', body: { name: bookName } }); return `已改名 → ${data.name}`; });

  return (
    <Section
      title={<span>{book.name} <span className="ml-2 font-mono text-[11px] text-zinc-500">{book.id}</span></span>}
      actions={
        <>
          <button className="btn-ghost" onClick={() => void apiDownload(`/api/world-books/${book.id}/export`, `${book.name}.json`).catch((e) => setErr(describeError(e)))}>导出 JSON</button>
          {!book.deleted_at && <button className="btn-ghost" title="POST /api/world-books/{id}/duplicate" onClick={() => void run(async () => { const { data } = await api<{ name: string }>(`/api/world-books/${book.id}/duplicate`, { method: 'POST' }); return `已复制 → ${data.name}`; })}>复制</button>}
          {book.deleted_at ? <button className="btn-ghost" onClick={() => void run(async () => { const { data } = await api<Book>(`/api/world-books/${book.id}/restore`, { method: 'POST' }); return `已恢复 → ${data.name}`; })}>恢复</button>
            : !locked && <button className="btn-ghost text-rose-300" onClick={() => void (async () => { try { await api(`/api/world-books/${book.id}`, { method: 'DELETE' }); onClosed(); } catch (e) { setErr(describeError(e)); } })()}>删除</button>}
          <button className="btn-ghost" onClick={onClosed}>关闭</button>
        </>
      }
    >
      {locked && <div className="text-xs text-amber-200/90">系统世界书不可改内容、不可删除。全局开关和复制仍可用，改条目请先复制。</div>}
      <div className="flex items-center gap-2 text-xs text-zinc-400">
        {!locked && <CountedField limit={NAME_MAX} className="input w-64" value={bookName} onChange={setBookName} />}
        {!locked && <button className="btn-ghost" onClick={() => void rename()}>改名</button>}
        <span>被引用：{book.referenced_by.length ? book.referenced_by.map((r) => r.name).join(', ') : '无'}</span>
      </div>
      <Message text={err} kind="error" />
      <Message text={msg} kind="ok" />
      <div className="max-h-72 overflow-auto rounded-lg border border-zinc-800">
        <table className="w-full text-left text-xs">
          <thead className="bg-zinc-900 text-zinc-400"><tr><th className="px-2 py-1">uid</th><th className="px-2 py-1">comment</th><th className="px-2 py-1">key</th><th className="px-2 py-1">content</th><th className="px-2 py-1">flags</th><th className="px-2 py-1"></th></tr></thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.uid} className="border-t border-zinc-800 align-top">
                <td className="px-2 py-1 font-mono">{e.uid}</td>
                <td className="px-2 py-1">{e.comment}</td>
                <td className="px-2 py-1 text-zinc-400">{e.key.join(', ')}</td>
                <td className="max-w-xs truncate px-2 py-1 text-zinc-400" title={e.content}>{e.content.slice(0, 80)}</td>
                <td className="px-2 py-1 space-x-1">{e.constant && <Pill tone="indigo">constant</Pill>}{e.disable && <Pill tone="rose">disabled</Pill>}{e.vectorized && <Pill tone="emerald">vectorized</Pill>}<Pill>order {e.order}</Pill><Pill>pos {e.position}</Pill></td>
                <td className="px-2 py-1 whitespace-nowrap">{!locked && <><button className="text-indigo-300" onClick={() => { setEditing(e); one.reset(e); }}>编辑</button> · <button className="text-emerald-300" title="change 15：vectorized 条目不做关键词扫描，只在 extension_settings.vectors.enabled_world_info 开启时按语义激活" onClick={() => void toggleVectorized(e)}>{e.vectorized ? '取消语义' : '语义'}</button> · <button className="text-rose-300" onClick={() => void delEntry(e.uid)}>删</button></>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!locked && <div className="flex gap-2"><button className="btn-ghost" onClick={() => void addEntry()}>+ 条目</button></div>}
      {editing && (
        <div>
          <div className="flex items-center justify-between"><span className="label">编辑条目 uid={editing.uid}（PATCH 部分字段）</span><div className="flex gap-2"><button className="btn" onClick={saveOne}>保存</button><button className="btn-ghost" onClick={() => setEditing(null)}>取消</button></div></div>
          <JsonEditor value={one.text} onChange={one.setText} error={one.error} rows={14} />
        </div>
      )}
      <div>
        <div className="flex items-center justify-between"><span className="label">全部条目（PUT 整体替换，保留 uid）</span>{!locked && <button className="btn-ghost" onClick={saveAll}>保存全部</button>}</div>
        <JsonEditor value={all.text} onChange={all.setText} error={all.error} rows={12} />
      </div>
    </Section>
  );
}
